import React, { useCallback, useMemo } from "react";
import { Dialog } from "@blueprintjs/core";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import CompletenessIndicator from "components/CompletenessIndicator";
import Input from "rs-components/Input";
import RenamableInput from "rs-components/Input/components/RenamableInput";
import { RenamableStyles } from "components/Renamable/components/RenamableCore";
import ScrollBar from "components/ScrollBar";
import Button, { ButtonSize, ButtonStyle } from "components/Button";
import UsedInResultsIcon from "./components/UsedInResultsIcon";
import ResultRecalculationDialog from "./components/ResultsRecalculationDialog";
import { INCORRECT_INPUTS_MESSAGE, DIALOG_ACTION_TITLE } from "shared/labels";
import { isFormChanged, isFormValid } from "shared/validators";
import { isEditableSelector } from "store/knowledge-base-item/knowledge-base-item.selectors";
import { numberOfResultsScenarioEntityUsedInSelector } from "store/rs-workspace/selectors/results.selectors";
import { isResultElementUsedInCalculationsSelector } from "store/rs-workspace/selectors/results.selectors";
import { setResultsRecalculationDialogOpen } from "store/rs-workspace/actions/rs-workspace.actions";
import { isNotEmpty } from "utils/validate";
import * as styles from "./styles.module.scss";
const EntityDialog = ({ nameFieldName, nameFieldRestrictions, entityType, isOpen, handleSubmit, onCancel, onDialogClose, onDialogClosed, onDialogOpened, onDialogOpening, onClosing, entityCompleted = false, children, formRenderProps, selectedId, headerSuffix, index, enableSubmitIfPristine = false, fieldsNotTrackedForChange, }) => {
    const dispatch = useDispatch();
    const entityUsedInResultsCount = useSelector(numberOfResultsScenarioEntityUsedInSelector(entityType));
    const isResultElementUsedInCalculations = useSelector(isResultElementUsedInCalculationsSelector());
    const editable = useSelector(isEditableSelector);
    const handleCancel = useCallback(() => {
        onDialogClose();
        if (onCancel) {
            onCancel();
        }
    }, [onCancel, onDialogClose]);
    const onSubmit = useCallback(({ dirtyFields }) => {
        const regularFieldChanged = Object.keys(dirtyFields).some((key) => Boolean(dirtyFields[key]) && !fieldsNotTrackedForChange?.includes(key));
        if ((entityUsedInResultsCount || isResultElementUsedInCalculations) && regularFieldChanged) {
            dispatch(setResultsRecalculationDialogOpen(true));
            return;
        }
        handleSubmit();
    }, [
        entityUsedInResultsCount,
        isResultElementUsedInCalculations,
        fieldsNotTrackedForChange,
        handleSubmit,
        dispatch,
    ]);
    const isOkButtonDisabled = useMemo(() => !isFormValid(formRenderProps), [formRenderProps]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Dialog, { isOpen: isOpen, className: styles.dialog, onClose: onDialogClose, onClosed: onDialogClosed, title: null, autoFocus: true, canEscapeKeyClose: false, canOutsideClickClose: false, enforceFocus: true, onOpening: onDialogOpening, onOpened: onDialogOpened, onClosing: onClosing, transitionDuration: 0 },
            React.createElement("div", { className: styles.contentWrapper },
                React.createElement("div", { className: styles.header },
                    React.createElement(CompletenessIndicator, { complete: entityCompleted }),
                    isNotEmpty(index) && React.createElement("span", { className: styles.index },
                        index,
                        "."),
                    React.createElement(Input, { component: RenamableInput, name: nameFieldName, restrictions: nameFieldRestrictions, classes: {
                            container: classNames(styles.renamableRoot, { [styles.editable]: editable }),
                            component: styles.renamableContainer,
                        }, style: RenamableStyles.fullWidth, entityType: entityType, readonly: !editable, clearable: !nameFieldRestrictions?.IsMandatory }),
                    React.createElement("div", { className: styles.controls },
                        entityUsedInResultsCount > 0 && (React.createElement(UsedInResultsIcon, { entityType: entityType, count: entityUsedInResultsCount })),
                        headerSuffix,
                        React.createElement(Button, { onClick: handleCancel, classes: { root: styles.button }, label: editable ? DIALOG_ACTION_TITLE.CANCEL : DIALOG_ACTION_TITLE.CLOSE, role: "button", style: editable ? ButtonStyle.Secondary : ButtonStyle.Primary, size: ButtonSize.Normal, showTooltipIfDisabled: true }),
                        editable && (React.createElement(Button, { classes: { root: styles.button }, label: DIALOG_ACTION_TITLE.OK, role: "button", style: ButtonStyle.Primary, size: ButtonSize.Normal, showTooltipIfDisabled: true, onClick: () => isFormChanged(formRenderProps, selectedId) || !!enableSubmitIfPristine
                                ? onSubmit(formRenderProps)
                                : handleCancel(), disabled: isOkButtonDisabled, title: !isFormValid(formRenderProps) ? INCORRECT_INPUTS_MESSAGE : "" })))),
                React.createElement("div", { className: styles.scrollBarWrapper },
                    React.createElement(ScrollBar, null,
                        React.createElement("div", { className: styles.content }, children))))),
        React.createElement(ResultRecalculationDialog, { onSubmit: handleSubmit, entityType: entityType })));
};
export default EntityDialog;
